import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  const values = [
    {
      title: 'Quality First',
      description: 'We source only authentic products from authorized distributors and manufacturers',
      color: 'bg-blue-50',
      icon: 'ri-shield-check-line',
      iconColor: 'text-blue-500'
    },
    {
      title: 'Customer Focus',
      description: 'Your satisfaction is our priority with dedicated support and easy returns',
      color: 'bg-orange-50',
      icon: 'ri-customer-service-2-line',
      iconColor: 'text-orange-500'
    },
    {
      title: 'Innovation',
      description: 'Bringing you the latest technology and cutting-edge gaming experiences',
      color: 'bg-purple-50',
      icon: 'ri-lightbulb-flash-line',
      iconColor: 'text-purple-500'
    },
    {
      title: 'Trust',
      description: 'Building lasting relationships through transparency and reliability',
      color: 'bg-green-50',
      icon: 'ri-hand-heart-line',
      iconColor: 'text-green-500'
    }
  ];

  const team = [
    {
      name: 'Sarah Chen',
      role: 'Founder & CEO',
      image: 'https://public.readdy.ai/ai/img_res/c2916b51a14b06f135523688c9e75c5c.jpg'
    },
    {
      name: 'Marcus Rodriguez',
      role: 'Head of Operations',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Hispanic%20man%20business%20executive%20in%20modern%20office%20wearing%20suit%20confident%20expression%20clean%20white%20background%20professional%20corporate%20headshot%20photography&width=600&height=600&seq=team002&orientation=squarish'
    },
    {
      name: 'Emily Watson',
      role: 'Customer Success Lead',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Caucasian%20woman%20business%20executive%20with%20friendly%20smile%20wearing%20professional%20attire%20clean%20white%20background%20professional%20corporate%20headshot%20photography&width=600&height=600&seq=team003&orientation=squarish'
    },
    {
      name: 'David Kim',
      role: 'Tech Specialist',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20man%20tech%20specialist%20wearing%20casual%20business%20attire%20friendly%20expression%20clean%20white%20background%20professional%20corporate%20headshot%20photography&width=600&height=600&seq=team004&orientation=squarish'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-20">
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-block border border-gray-300 px-5 py-2 rounded-full mb-8">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Our Story</span>
            </div>
            
            <h1 className="text-6xl font-thin text-gray-900 leading-tight mb-12">
              Empowering Gamers & Creators<br />
              With Cutting-Edge Technology<br />
              Since 2013
            </h1>
            
            <div className="space-y-8 text-lg text-gray-700 leading-relaxed">
              <p>
                <strong>Tech Cell Corp</strong> was born from a passion for gaming and technology. We started as a small team of enthusiasts who believed that everyone deserves access to premium gaming consoles, high-performance computers, and quality accessories without the hassle.
              </p>
              
              <p>
                What began in a small warehouse has grown into a trusted destination for thousands of gamers, content creators, and tech professionals. We've built our reputation on three pillars: <strong>authenticity</strong>, <strong>customer service</strong>, and <strong>competitive pricing</strong>.
              </p>
              
              <p>
                Today, we partner directly with leading manufacturers like Sony, Microsoft, ASUS, and more to bring you the latest releases and exclusive deals. Our team of tech specialists carefully curates every product, ensuring you get exactly what you need to level up your setup.
              </p>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-[#F5F5F7]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">Our Values</h2>
              <p className="text-lg text-gray-600">The principles that guide everything we do</p>
            </div>
            
            <div className="grid grid-cols-4 gap-6">
              {values.map((value, index) => (
                <div key={index} className={`${value.color} rounded-3xl p-8 h-80 flex flex-col`}>
                  <div className="flex-1 flex items-center justify-center mb-6">
                    <div className="w-20 h-20 flex items-center justify-center bg-white rounded-2xl shadow-sm">
                      <i className={`${value.icon} ${value.iconColor} text-4xl`}></i>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
              <p className="text-lg text-gray-600">The people behind your perfect tech experience</p>
            </div>
            
            <div className="grid grid-cols-4 gap-8">
              {team.map((member, index) => (
                <div key={index} className="text-center group">
                  <div className="aspect-square rounded-3xl overflow-hidden mb-6 bg-gray-100">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-sm text-gray-600">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E8E]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-4 gap-12 text-center text-white">
              <div>
                <div className="text-6xl font-black mb-3">50K+</div>
                <p className="text-lg text-white/90">Happy Customers</p>
              </div>
              <div>
                <div className="text-6xl font-black mb-3">500+</div>
                <p className="text-lg text-white/90">Products Available</p>
              </div>
              <div>
                <div className="text-6xl font-black mb-3">4.9</div>
                <p className="text-lg text-white/90">Average Rating</p>
              </div>
              <div>
                <div className="text-6xl font-black mb-3">24/7</div>
                <p className="text-lg text-white/90">Customer Support</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-5xl font-bold text-gray-900 mb-8">Our Mission</h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-12">
              To make premium gaming and computing technology accessible to everyone, backed by exceptional service and expert guidance. We believe that the right equipment can unlock creativity, enhance performance, and bring joy to millions of users worldwide.
            </p>
            <div className="flex items-center justify-center gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FF6B6B] text-white rounded-2xl">
                <i className="ri-rocket-line text-3xl"></i>
              </div>
              <div className="w-16 h-16 flex items-center justify-center bg-[#4ECDC4] text-white rounded-2xl">
                <i className="ri-gamepad-line text-3xl"></i>
              </div>
              <div className="w-16 h-16 flex items-center justify-center bg-[#F4A261] text-white rounded-2xl">
                <i className="ri-computer-line text-3xl"></i>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
